import type { GameState } from './state/gameState';
import { createGameState, nextId } from './state/gameState';
import { Emitter } from './events';
import type { GameEventMap } from './events';
import type { Rng } from './rng';
import { createRng, randomSeed } from './rng';
import type { MatchRule } from './rules/matchRule';
import { getRule } from './rules';
import type { GameMode } from './config/modes';
import { DEFAULT_MODE, stageFor } from './config/modes';
import type { PaceParams } from './config/gameConfig';
import { SCORING, ARENA, PROJECTILE, PACE_PRESETS, DEFAULT_PACE, POWERUPS } from './config/gameConfig';
import type { ProjectileState } from './types';
import { clone, sub, normalize, distanceSq } from './math/vec3';
import { updateSpawnSystem } from './systems/spawnSystem';
import { updateEnemySystem } from './systems/enemySystem';
import { updateProjectileSystem } from './systems/projectileSystem';
import { updatePowerUpSystem } from './systems/powerUpSystem';

export interface WorldOptions {
  mode?: GameMode;
  seed?: number;
  /** Pace preset id (see PACE_PRESETS): relaxed / standard / fast. */
  pace?: string;
}

// The headless game. Owns the authoritative state, steps the systems, and emits
// events. The only way the outside world drives it is through these commands —
// the input layer translates device events into these calls, so the same World
// works on desktop, mobile, or a server.
export class World {
  readonly events = new Emitter<GameEventMap>();
  state: GameState;
  private rng: Rng;
  private mode: GameMode;
  private rule: MatchRule;
  private paceId: string;

  constructor(opts: WorldOptions = {}) {
    this.mode = opts.mode ?? DEFAULT_MODE;
    this.rule = getRule(this.startingRuleId(this.mode));
    this.paceId = opts.pace && PACE_PRESETS[opts.pace] ? opts.pace : DEFAULT_PACE;
    const seed = opts.seed ?? randomSeed();
    this.state = createGameState(seed);
    this.rng = createRng(seed);
  }

  get currentRule(): MatchRule {
    return this.rule;
  }

  get currentMode(): GameMode {
    return this.mode;
  }

  get currentPace(): string {
    return this.paceId;
  }

  private startingRuleId(mode: GameMode): string {
    return mode.stages?.[0]?.ruleId ?? mode.ruleId;
  }

  private get pace(): PaceParams {
    return PACE_PRESETS[this.paceId];
  }

  start(): void {
    const seed = this.state.seed;
    this.state = createGameState(seed);
    this.rng = createRng(seed);
    this.rule = getRule(this.startingRuleId(this.mode));
    this.state.status = 'playing';
    this.events.emit('started', { seed });
    this.events.emit('statusChanged', { status: 'playing' });
    this.events.emit('scoreChanged', { score: this.state.score, delta: 0 });
    this.events.emit('weaponChanged', { weapon: this.state.weapon });
  }

  reset(): void {
    this.state = createGameState(randomSeed());
    this.rng = createRng(this.state.seed);
    this.rule = getRule(this.startingRuleId(this.mode));
    this.events.emit('reset', undefined);
    this.events.emit('statusChanged', { status: 'idle' });
  }

  setMode(mode: GameMode): void {
    this.mode = mode;
    this.rule = getRule(this.startingRuleId(mode));
  }

  setPace(paceId: string): void {
    if (PACE_PRESETS[paceId]) this.paceId = paceId;
  }

  setPaused(paused: boolean): void {
    if (this.state.status === 'gameover' || this.state.status === 'idle') return;
    this.state.status = paused ? 'paused' : 'playing';
    this.events.emit('paused', { paused });
    this.events.emit('statusChanged', { status: this.state.status });
  }

  togglePause(): void {
    this.setPaused(this.state.status === 'playing');
  }

  changeWeapon(delta: number): void {
    if (this.state.status !== 'playing') return;
    this.state.weapon = (this.state.weapon + delta + 10) % 10;
    this.addScore(SCORING.weaponChange);
    this.events.emit('weaponChanged', { weapon: this.state.weapon });
  }

  setWeapon(weapon: number): void {
    if (this.state.status !== 'playing') return;
    const next = ((weapon % 10) + 10) % 10;
    if (next === this.state.weapon) return;
    this.state.weapon = next;
    this.addScore(SCORING.weaponChange);
    this.events.emit('weaponChanged', { weapon: this.state.weapon });
  }

  /** Fire at the nearest enemy with the selected answer. While the multi-shot
   *  power-up runs, one trigger pull volleys at several nearest enemies. */
  shoot(): void {
    if (this.state.status !== 'playing') return;
    if (this.state.score <= SCORING.floor) {
      this.events.emit('outOfPoints', undefined);
      return;
    }
    const multiShot = this.state.elapsed < this.state.effects.multiShotUntil;
    const targets = this.nearestEnemies(multiShot ? POWERUPS.multiShotTargets : 1);
    if (targets.length === 0) return;

    this.addScore(SCORING.shot);

    for (const target of targets) {
      const origin = clone(ARENA.playerPosition);
      const projectile: ProjectileState = {
        id: nextId(this.state),
        weapon: this.state.weapon,
        position: origin,
        direction: normalize(sub(target.position, origin)),
        speed: PROJECTILE.speed,
        targetId: target.id,
        bornAt: this.state.elapsed,
        ttl: PROJECTILE.ttl,
      };
      this.state.projectiles.push(projectile);
      this.events.emit('projectileFired', { projectile });
    }
  }

  /** Advance the simulation by `dt` seconds. No-op unless actively playing. */
  update(dt: number): void {
    if (this.state.status !== 'playing') return;
    this.state.elapsed += dt;

    updateSpawnSystem(this.state, dt, this.rng, this.rule, this.pace, this.events);
    this.applyStage();
    updateProjectileSystem(this.state, dt, this.rule, this.events, (d) => this.addScore(d));
    updatePowerUpSystem(this.state, dt, this.events);
    updateEnemySystem(this.state, dt, this.events);
    this.updateHint();
  }

  /** Hint power-up: mark the enemy the CURRENT weapon would destroy so the
   *  render layer can pulse it. Recomputed per tick — weapon changes included. */
  private updateHint(): void {
    if (this.state.elapsed >= this.state.effects.hintUntil) {
      this.state.hintTargetId = null;
      return;
    }
    const weapon = this.state.weapon;
    const found = this.state.enemies.find((e) => this.rule.matches(weapon, e.value));
    this.state.hintTargetId = found?.id ?? null;
  }

  /** Curriculum ladders: when the level crosses into a new stage, swap the
   *  rule, clear the board (a celebration beat — and it sidesteps live enemies
   *  being judged by a rule they weren't spawned under), and ease the pace by
   *  making difficulty band-relative via state.stageFromLevel. */
  private applyStage(): void {
    const stage = stageFor(this.mode, this.state.level);
    if (!stage || stage.ruleId === this.rule.id) return;
    this.rule = getRule(stage.ruleId);
    this.state.stageFromLevel = stage.fromLevel;
    this.state.enemies.length = 0;
    this.state.projectiles.length = 0;
    this.state.powerups.length = 0;
    this.events.emit('stageChanged', {
      ruleId: this.rule.id,
      label: this.rule.label,
      instructions: this.rule.instructions,
      banner: stage.banner,
      level: this.state.level,
    });
  }

  private nearestEnemies(count: number) {
    if (count <= 1) {
      let best = null;
      let bestDist = Infinity;
      for (const enemy of this.state.enemies) {
        const d = distanceSq(enemy.position, ARENA.playerPosition);
        if (d < bestDist) {
          bestDist = d;
          best = enemy;
        }
      }
      return best ? [best] : [];
    }
    return [...this.state.enemies]
      .sort(
        (a, b) =>
          distanceSq(a.position, ARENA.playerPosition) - distanceSq(b.position, ARENA.playerPosition),
      )
      .slice(0, count);
  }

  private addScore(delta: number): void {
    const next = Math.max(SCORING.floor, this.state.score + delta);
    const applied = next - this.state.score;
    if (applied === 0) return;
    this.state.score = next;
    this.events.emit('scoreChanged', { score: this.state.score, delta: applied });
  }
}
